import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ExternalLink,
  KeyRound,
  Link2,
  Loader2,
  Search,
  ShieldOff,
  ShieldPlus,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import { api } from "../../lib/axios";
import { useAuth } from "../../context/AuthContext";

const MSG_ADMIN = "Action réservée administrateur";

function getApiErrorMessage(e: unknown, fallback: string): string {
  const ax = e instanceof AxiosError ? e : null;
  const d = ax?.response?.data as { message?: string } | undefined;
  return d?.message?.trim() || fallback;
}

export type ClientPortalAccessManagerProps = {
  open: boolean;
  onClose: () => void;
  /** Après mutation : rafraîchir KPI + tableau parent */
  onDidChange: () => void;
};

type ClientRow = {
  id: number;
  nomAffiche: string;
  telephone: string;
  email: string;
  abonnementActif?: boolean;
  zoneLabel?: string | null;
  demandesEnAttente?: number;
  liaisonsPortail?: number;
};

type PortalUserRow = {
  id: number;
  email: string | null;
  identifiant: string | null;
  nom: string;
  actif: boolean;
  createdAt: string;
  clientId: number | null;
  clientNomAffiche: string | null;
  derniereConnexion: string | null;
};

type OrphanRow = {
  id: number;
  email: string | null;
  identifiant: string | null;
  nom: string;
  prenom: string | null;
  actif: boolean;
  createdAt: string;
};

type TabId = "sans-acces" | "lies" | "orphelins";

export default function ClientPortalAccessManager({
  open,
  onClose,
  onDidChange,
}: ClientPortalAccessManagerProps): React.JSX.Element | null {
  const { user } = useAuth();
  const isAdmin = String(user?.role ?? "").toLowerCase() === "admin";

  const [tab, setTab] = useState<TabId>("sans-acces");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const [sansAcces, setSansAcces] = useState<ClientRow[]>([]);
  const [lies, setLies] = useState<PortalUserRow[]>([]);
  const [orphelins, setOrphelins] = useState<OrphanRow[]>([]);
  const [clientsPick, setClientsPick] = useState<ClientRow[]>([]);

  const [createForClient, setCreateForClient] = useState<ClientRow | null>(null);
  const [linkForClient, setLinkForClient] = useState<ClientRow | null>(null);
  const [linkOrphanForClient, setLinkOrphanForClient] = useState<OrphanRow | null>(null);
  const [resetClientId, setResetClientId] = useState<number | null>(null);

  const [formEmail, setFormEmail] = useState("");
  const [formIdent, setFormIdent] = useState("");
  const [formTel, setFormTel] = useState("");
  const [formPass, setFormPass] = useState("");
  const [pickOrphanId, setPickOrphanId] = useState("");
  const [pickClientForOrphan, setPickClientForOrphan] = useState("");
  const [resetPass, setResetPass] = useState("");
  const [resetPassConfirm, setResetPassConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sans, pu, orp, pick] = await Promise.all([
        api.get<{ items?: ClientRow[] }>("/client-portal/admin/clients", {
          params: { withoutPortal: true, limit: 100, page: 1 },
        }),
        api.get<{ items?: PortalUserRow[] }>("/client-portal/admin/portal-users"),
        api.get<{ items?: OrphanRow[] }>("/client-portal/admin/orphan-portal-users"),
        api.get<{ items?: ClientRow[] }>("/client-portal/admin/clients", {
          params: { limit: 200, page: 1 },
        }),
      ]);
      setSansAcces(sans.data.items ?? []);
      setLies(pu.data.items ?? []);
      setOrphelins(orp.data.items ?? []);
      setClientsPick(pick.data.items ?? []);
    } catch {
      toast.error("Impossible de charger les données de gestion des accès.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    void loadAll();
  }, [open, loadAll]);

  const filterStr = search.trim().toLowerCase();
  const filteredSans = useMemo(() => {
    if (!filterStr) return sansAcces;
    return sansAcces.filter(
      (c) =>
        c.nomAffiche.toLowerCase().includes(filterStr) ||
        c.telephone.toLowerCase().includes(filterStr) ||
        c.email.toLowerCase().includes(filterStr) ||
        String(c.id).includes(filterStr)
    );
  }, [sansAcces, filterStr]);

  const filteredLies = useMemo(() => {
    if (!filterStr) return lies;
    return lies.filter(
      (u) =>
        (u.clientNomAffiche ?? "").toLowerCase().includes(filterStr) ||
        (u.email ?? "").toLowerCase().includes(filterStr) ||
        (u.identifiant ?? "").toLowerCase().includes(filterStr) ||
        u.nom.toLowerCase().includes(filterStr) ||
        String(u.id).includes(filterStr)
    );
  }, [lies, filterStr]);

  const filteredOrphelins = useMemo(() => {
    if (!filterStr) return orphelins;
    return orphelins.filter(
      (u) =>
        (u.email ?? "").toLowerCase().includes(filterStr) ||
        (u.identifiant ?? "").toLowerCase().includes(filterStr) ||
        u.nom.toLowerCase().includes(filterStr) ||
        String(u.id).includes(filterStr)
    );
  }, [orphelins, filterStr]);

  const openCreate = (c: ClientRow) => {
    setCreateForClient(c);
    setFormEmail(c.email !== "—" ? c.email : "");
    setFormIdent("");
    setFormTel(c.telephone !== "—" ? c.telephone : "");
    setFormPass("");
  };

  const submitCreate = async () => {
    if (!createForClient) return;
    const pwd = formPass.trim();
    if (pwd.length < 6) {
      toast.warning("Mot de passe temporaire : au moins 6 caractères.");
      return;
    }
    const em = formEmail.trim();
    const idt = formIdent.trim();
    if (!em && !idt) {
      toast.warning("Renseignez un e-mail ou un identifiant de connexion.");
      return;
    }
    if (em && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) {
      toast.warning("E-mail invalide.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/client-portal/admin/create-portal-user", {
        clientId: createForClient.id,
        email: em || undefined,
        identifiant: idt || undefined,
        telephone: formTel.trim() || undefined,
        temporaryPassword: pwd,
      });
      toast.success("Compte portail créé");
      setCreateForClient(null);
      await loadAll();
      onDidChange();
    } catch (e) {
      const ax = e instanceof AxiosError ? e : null;
      const err = ax?.response?.data as { error?: string } | undefined;
      if (ax?.response?.status === 403) {
        toast.error(getApiErrorMessage(e, MSG_ADMIN));
      } else if (ax?.response?.status === 409 && err?.error === "CLIENT_PORTAL_ALREADY_EXISTS") {
        toast.error("Ce client possède déjà un compte portail.");
      } else if (ax?.response?.status === 409) {
        toast.error("E-mail ou identifiant déjà utilisé.");
      } else {
        toast.error("Création impossible.");
      }
    } finally {
      setBusy(false);
    }
  };

  const submitLinkExisting = async () => {
    if (!linkForClient) return;
    const uid = Number(pickOrphanId);
    if (!uid) {
      toast.warning("Choisissez un compte CLIENT orphelin.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/client-portal/admin/clients/${linkForClient.id}/link-portal-user`, {
        userId: uid,
      });
      toast.success("Compte portail lié");
      setLinkForClient(null);
      setPickOrphanId("");
      await loadAll();
      onDidChange();
    } catch (e) {
      const ax = e instanceof AxiosError ? e : null;
      const err = ax?.response?.data as { error?: string; message?: string } | undefined;
      if (ax?.response?.status === 403) {
        toast.error(getApiErrorMessage(e, MSG_ADMIN));
      } else if (err?.error === "CLIENT_PORTAL_ALREADY_EXISTS") {
        toast.error("Ce client possède déjà un compte portail.");
      } else {
        toast.error(err?.message ?? "Liaison impossible.");
      }
    } finally {
      setBusy(false);
    }
  };

  const submitLinkOrphanToClient = async () => {
    if (!linkOrphanForClient) return;
    const cid = Number(pickClientForOrphan);
    if (!cid) {
      toast.warning("Choisissez une fiche client.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/client-portal/admin/link-portal-user", {
        userId: linkOrphanForClient.id,
        clientId: cid,
      });
      toast.success("Compte portail lié");
      setLinkOrphanForClient(null);
      setPickClientForOrphan("");
      await loadAll();
      onDidChange();
    } catch (e) {
      const ax = e instanceof AxiosError ? e : null;
      const err = ax?.response?.data as { error?: string; message?: string } | undefined;
      if (ax?.response?.status === 403) {
        toast.error(getApiErrorMessage(e, MSG_ADMIN));
      } else if (err?.error === "CLIENT_PORTAL_ALREADY_EXISTS") {
        toast.error("Ce client possède déjà un compte portail.");
      } else {
        toast.error(err?.message ?? "Liaison impossible.");
      }
    } finally {
      setBusy(false);
    }
  };

  const setUserAccess = async (clientId: number, active: boolean) => {
    setBusy(true);
    try {
      await api.patch(`/client-portal/admin/clients/${clientId}/access`, { active });
      toast.success(active ? "Accès activé" : "Accès désactivé");
      await loadAll();
      onDidChange();
    } catch (e) {
      const ax = e instanceof AxiosError ? e : null;
      if (ax?.response?.status === 403) {
        toast.error(getApiErrorMessage(e, MSG_ADMIN));
      } else if (ax?.response?.status === 404) {
        toast.error("Aucun compte portail lié à ce client.");
      } else {
        toast.error("Action impossible.");
      }
    } finally {
      setBusy(false);
    }
  };

  const submitReset = async () => {
    if (!resetClientId) return;
    const pwd = resetPass.trim();
    const pwd2 = resetPassConfirm.trim();
    if (pwd.length < 6) {
      toast.warning("Mot de passe temporaire : au moins 6 caractères.");
      return;
    }
    if (pwd !== pwd2) {
      toast.warning("Les deux saisies du mot de passe ne correspondent pas.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/client-portal/admin/clients/${resetClientId}/reset-password`, {
        temporaryPassword: pwd,
      });
      toast.success("Mot de passe réinitialisé");
      setResetClientId(null);
      setResetPass("");
      setResetPassConfirm("");
      await loadAll();
      onDidChange();
    } catch (e) {
      const ax = e instanceof AxiosError ? e : null;
      if (ax?.response?.status === 403) {
        toast.error(getApiErrorMessage(e, MSG_ADMIN));
      } else if (ax?.response?.status === 404) {
        toast.error("Aucun compte portail pour ce client.");
      } else {
        toast.error("Réinitialisation impossible.");
      }
    } finally {
      setBusy(false);
    }
  };

  const setOrphanAccess = async (id: number, active: boolean) => {
    setBusy(true);
    try {
      await api.patch(`/client-portal/admin/orphan-portal-users/${id}/access`, { active });
      toast.success(active ? "Accès activé" : "Accès désactivé");
      await loadAll();
      onDidChange();
    } catch (e) {
      const ax = e instanceof AxiosError ? e : null;
      if (ax?.response?.status === 403) {
        toast.error(getApiErrorMessage(e, MSG_ADMIN));
      } else {
        toast.error("Action impossible.");
      }
    } finally {
      setBusy(false);
    }
  };

  const deleteOrphan = async (id: number) => {
    if (!window.confirm("Supprimer définitivement ce compte CLIENT orphelin ?")) return;
    setBusy(true);
    try {
      await api.delete(`/client-portal/admin/orphan-portal-users/${id}`);
      toast.success("Compte supprimé");
      await loadAll();
      onDidChange();
    } catch (e) {
      const ax = e instanceof AxiosError ? e : null;
      if (ax?.response?.status === 403) {
        toast.error(getApiErrorMessage(e, MSG_ADMIN));
      } else {
        toast.error("Suppression impossible.");
      }
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  const clientOrigin = window.location.origin;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-black/70 p-4 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="access-manager-title"
    >
      <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-slate-900 to-slate-950 shadow-2xl shadow-black/50">
        <div className="flex items-start justify-between gap-3 border-b border-white/10 px-5 py-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-widest text-emerald-400/90">
              Portail client
            </p>
            <h2 id="access-manager-title" className="text-xl font-semibold text-white">
              Gérer accès clients
            </h2>
            <p className="mt-1 text-sm text-slate-400">
              Création, liaison et cycle de vie des comptes{" "}
              <span className="text-emerald-300">CLIENT</span> (
              <code className="text-slate-300">User.clientId</code>).
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-400 transition hover:bg-white/5 hover:text-white"
            aria-label="Fermer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {!isAdmin ? (
          <div className="border-b border-amber-500/25 bg-amber-500/10 px-5 py-3 text-sm text-amber-100">
            {MSG_ADMIN}. Les créations, liaisons, activation / désactivation d’accès, réinitialisations et
            suppressions sont indisponibles pour votre rôle.
          </div>
        ) : null}

        <div className="flex flex-col gap-3 border-b border-white/10 px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2">
            {(
              [
                ["sans-acces", "Clients sans accès"],
                ["lies", "Comptes portail liés"],
                ["orphelins", "Comptes CLIENT orphelins"],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                onClick={() => setTab(id)}
                className={
                  tab === id
                    ? "rounded-lg bg-emerald-600/25 px-3 py-1.5 text-sm font-medium text-emerald-100 ring-1 ring-emerald-500/40"
                    : "rounded-lg px-3 py-1.5 text-sm text-slate-300 transition hover:bg-white/5"
                }
              >
                {label}
              </button>
            ))}
          </div>
          <div className="relative flex-1 sm:max-w-xs">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <input
              type="search"
              placeholder="Rechercher…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-lg border border-white/10 bg-slate-950 py-2 pl-9 pr-3 text-sm text-white placeholder:text-slate-500"
            />
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="flex justify-center py-16 text-slate-400">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : tab === "sans-acces" ? (
            <div className="overflow-x-auto rounded-xl border border-white/10">
              <table className="min-w-full divide-y divide-white/10 text-left text-sm">
                <thead className="bg-slate-900/90 text-xs uppercase tracking-wide text-slate-400">
                  <tr>
                    <th className="px-3 py-2 font-medium">Client</th>
                    <th className="px-3 py-2 font-medium">Téléphone</th>
                    <th className="px-3 py-2 font-medium">Email</th>
                    <th className="px-3 py-2 font-medium">Abonnement actif</th>
                    <th className="px-3 py-2 font-medium">Zone</th>
                    <th className="px-3 py-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {filteredSans.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-3 py-10 text-center text-slate-500">
                        Aucun client sans compte portail.
                      </td>
                    </tr>
                  ) : (
                    filteredSans.map((c) => (
                      <tr key={c.id} className="hover:bg-white/5">
                        <td className="px-3 py-2 font-medium text-white">{c.nomAffiche}</td>
                        <td className="px-3 py-2 text-slate-300">{c.telephone}</td>
                        <td className="px-3 py-2 text-slate-300">{c.email}</td>
                        <td className="px-3 py-2 text-slate-300">
                          {c.abonnementActif ? "Oui" : "Non"}
                        </td>
                        <td className="px-3 py-2 text-slate-300">{c.zoneLabel ?? "—"}</td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-1">
                            <button
                              type="button"
                              disabled={!isAdmin || busy}
                              title={!isAdmin ? MSG_ADMIN : undefined}
                              onClick={() => openCreate(c)}
                              className="rounded-md border border-emerald-500/35 bg-emerald-600/15 px-2 py-1 text-xs text-emerald-100 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                              Créer compte portail
                            </button>
                            <button
                              type="button"
                              disabled={!isAdmin || busy}
                              title={!isAdmin ? MSG_ADMIN : undefined}
                              onClick={() => {
                                setLinkForClient(c);
                                setPickOrphanId("");
                              }}
                              className="rounded-md border border-white/10 bg-slate-800 px-2 py-1 text-xs text-slate-200 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                              Lier compte existant
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          ) : tab === "lies" ? (
            <div className="overflow-x-auto rounded-xl border border-white/10">
              <table className="min-w-full divide-y divide-white/10 text-left text-sm">
                <thead className="bg-slate-900/90 text-xs uppercase tracking-wide text-slate-400">
                  <tr>
                    <th className="px-3 py-2 font-medium">Client</th>
                    <th className="px-3 py-2 font-medium">Email / identifiant</th>
                    <th className="px-3 py-2 font-medium">Statut accès</th>
                    <th className="px-3 py-2 font-medium">Dernière connexion</th>
                    <th className="px-3 py-2 font-medium">Créé le</th>
                    <th className="px-3 py-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {filteredLies.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-3 py-10 text-center text-slate-500">
                        Aucun compte lié.
                      </td>
                    </tr>
                  ) : (
                    filteredLies.map((u) => (
                      <tr key={u.id} className="hover:bg-white/5">
                        <td className="px-3 py-2 font-medium text-white">
                          {u.clientNomAffiche ?? "—"}{" "}
                          <span className="text-slate-500">#{u.clientId}</span>
                        </td>
                        <td className="px-3 py-2 text-slate-300">
                          {u.email ?? "—"}{" "}
                          {u.identifiant ? (
                            <span className="block text-xs text-slate-500">{u.identifiant}</span>
                          ) : null}
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className={
                              u.actif
                                ? "rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs text-emerald-300"
                                : "rounded-full bg-rose-500/15 px-2 py-0.5 text-xs text-rose-200"
                            }
                          >
                            {u.actif ? "Actif" : "Désactivé"}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-slate-400">
                          {u.derniereConnexion
                            ? new Date(u.derniereConnexion).toLocaleString("fr-FR")
                            : "—"}
                        </td>
                        <td className="px-3 py-2 text-slate-400">
                          {new Date(u.createdAt).toLocaleString("fr-FR")}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex max-w-[14rem] flex-wrap gap-1">
                            {u.actif ? (
                              <button
                                type="button"
                                disabled={!isAdmin || busy}
                                title={!isAdmin ? MSG_ADMIN : undefined}
                                onClick={() => u.clientId && void setUserAccess(u.clientId, false)}
                                className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-slate-800 px-2 py-1 text-xs text-slate-200 disabled:cursor-not-allowed disabled:opacity-40"
                              >
                                <ShieldOff className="h-3 w-3" />
                                Désactiver
                              </button>
                            ) : (
                              <button
                                type="button"
                                disabled={!isAdmin || busy}
                                title={!isAdmin ? MSG_ADMIN : undefined}
                                onClick={() => u.clientId && void setUserAccess(u.clientId, true)}
                                className="inline-flex items-center gap-1 rounded-md border border-emerald-500/30 bg-emerald-600/20 px-2 py-1 text-xs text-emerald-100 disabled:cursor-not-allowed disabled:opacity-40"
                              >
                                <ShieldPlus className="h-3 w-3" />
                                Activer
                              </button>
                            )}
                            <button
                              type="button"
                              disabled={!isAdmin || busy}
                              title={!isAdmin ? MSG_ADMIN : undefined}
                              onClick={() => {
                                if (u.clientId == null) return;
                                setResetClientId(u.clientId);
                                setResetPass("");
                                setResetPassConfirm("");
                              }}
                              className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-slate-800 px-2 py-1 text-xs text-slate-200 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                              <KeyRound className="h-3 w-3" />
                              Reset MDP
                            </button>
                            {u.clientId ? (
                              <Link
                                to={`/admin/clients/${u.clientId}`}
                                className="inline-flex rounded-md border border-white/10 bg-slate-800 px-2 py-1 text-xs text-slate-200"
                              >
                                Fiche client
                              </Link>
                            ) : null}
                            <a
                              href={`${clientOrigin}/client/login`}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 rounded-md border border-emerald-500/30 px-2 py-1 text-xs text-emerald-200"
                            >
                              <ExternalLink className="h-3 w-3" />
                              Portail
                            </a>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-white/10">
              <table className="min-w-full divide-y divide-white/10 text-left text-sm">
                <thead className="bg-slate-900/90 text-xs uppercase tracking-wide text-slate-400">
                  <tr>
                    <th className="px-3 py-2 font-medium">Nom utilisateur</th>
                    <th className="px-3 py-2 font-medium">Email</th>
                    <th className="px-3 py-2 font-medium">Identifiant</th>
                    <th className="px-3 py-2 font-medium">Créé le</th>
                    <th className="px-3 py-2 font-medium">Statut</th>
                    <th className="px-3 py-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {filteredOrphelins.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-3 py-10 text-center text-slate-500">
                        Aucun compte orphelin.
                      </td>
                    </tr>
                  ) : (
                    filteredOrphelins.map((u) => (
                      <tr key={u.id} className="hover:bg-white/5">
                        <td className="px-3 py-2 font-medium text-white">
                          {[u.prenom, u.nom].filter(Boolean).join(" ") || u.nom}
                        </td>
                        <td className="px-3 py-2 text-slate-300">{u.email ?? "—"}</td>
                        <td className="px-3 py-2 text-slate-300">{u.identifiant ?? "—"}</td>
                        <td className="px-3 py-2 text-slate-400">
                          {new Date(u.createdAt).toLocaleString("fr-FR")}
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className={
                              u.actif
                                ? "rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs text-emerald-300"
                                : "rounded-full bg-rose-500/15 px-2 py-0.5 text-xs text-rose-200"
                            }
                          >
                            {u.actif ? "Actif" : "Désactivé"}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-1">
                            <button
                              type="button"
                              disabled={!isAdmin || busy}
                              title={!isAdmin ? MSG_ADMIN : undefined}
                              onClick={() => {
                                setLinkOrphanForClient(u);
                                setPickClientForOrphan("");
                              }}
                              className="inline-flex items-center gap-1 rounded-md border border-emerald-500/35 bg-emerald-600/15 px-2 py-1 text-xs text-emerald-100 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                              <Link2 className="h-3 w-3" />
                              Lier à un client
                            </button>
                            {u.actif ? (
                              <button
                                type="button"
                                disabled={!isAdmin || busy}
                                title={!isAdmin ? MSG_ADMIN : undefined}
                                onClick={() => void setOrphanAccess(u.id, false)}
                                className="inline-flex items-center gap-1 rounded-md border border-white/10 px-2 py-1 text-xs text-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
                              >
                                <ShieldOff className="h-3 w-3" />
                                Désactiver
                              </button>
                            ) : (
                              <button
                                type="button"
                                disabled={!isAdmin || busy}
                                title={!isAdmin ? MSG_ADMIN : undefined}
                                onClick={() => void setOrphanAccess(u.id, true)}
                                className="inline-flex items-center gap-1 rounded-md border border-emerald-500/30 px-2 py-1 text-xs text-emerald-200 disabled:cursor-not-allowed disabled:opacity-40"
                              >
                                <ShieldPlus className="h-3 w-3" />
                                Activer
                              </button>
                            )}
                            <button
                              type="button"
                              disabled={!isAdmin || busy}
                              title={!isAdmin ? MSG_ADMIN : undefined}
                              onClick={() => void deleteOrphan(u.id)}
                              className="inline-flex items-center gap-1 rounded-md border border-white/10 px-2 py-1 text-xs text-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                              <Trash2 className="h-3 w-3" />
                              Supprimer
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {createForClient ? (
          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4">
            <div className="w-full max-w-md rounded-xl border border-white/10 bg-slate-900 p-4 shadow-xl">
              <h3 className="text-lg font-semibold text-white">Créer compte portail</h3>
              <p className="text-sm text-slate-400">
                Client #{createForClient.id} — {createForClient.nomAffiche}
              </p>
              <div className="mt-4 space-y-2">
                <input
                  type="email"
                  placeholder="E-mail (optionnel si identifiant)"
                  className="w-full rounded-md border border-white/10 bg-slate-950 px-3 py-2 text-sm text-white"
                  value={formEmail}
                  onChange={(e) => setFormEmail(e.target.value)}
                />
                <input
                  placeholder="Identifiant / téléphone connexion (optionnel si e-mail)"
                  className="w-full rounded-md border border-white/10 bg-slate-950 px-3 py-2 text-sm text-white"
                  value={formIdent}
                  onChange={(e) => setFormIdent(e.target.value)}
                />
                <input
                  placeholder="Téléphone (optionnel)"
                  className="w-full rounded-md border border-white/10 bg-slate-950 px-3 py-2 text-sm text-white"
                  value={formTel}
                  onChange={(e) => setFormTel(e.target.value)}
                />
                <input
                  type="password"
                  placeholder="Mot de passe temporaire (min. 6)"
                  className="w-full rounded-md border border-white/10 bg-slate-950 px-3 py-2 text-sm text-white"
                  value={formPass}
                  onChange={(e) => setFormPass(e.target.value)}
                  autoComplete="new-password"
                />
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setCreateForClient(null)}
                  className="rounded-lg px-3 py-2 text-sm text-slate-300 hover:bg-white/5"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  disabled={!isAdmin || busy}
                  onClick={() => void submitCreate()}
                  className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                >
                  Créer
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {linkForClient ? (
          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4">
            <div className="w-full max-w-md rounded-xl border border-white/10 bg-slate-900 p-4 shadow-xl">
              <h3 className="text-lg font-semibold text-white">Lier un compte existant</h3>
              <p className="text-sm text-slate-400">
                {linkForClient.nomAffiche} (#{linkForClient.id})
              </p>
              <select
                className="mt-4 w-full rounded-md border border-white/10 bg-slate-950 px-3 py-2 text-sm text-white"
                value={pickOrphanId}
                onChange={(e) => setPickOrphanId(e.target.value)}
              >
                <option value="">— Compte CLIENT orphelin —</option>
                {orphelins.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.nom} — {o.email ?? o.identifiant ?? `#${o.id}`}
                  </option>
                ))}
              </select>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setLinkForClient(null)}
                  className="rounded-lg px-3 py-2 text-sm text-slate-300 hover:bg-white/5"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  disabled={!isAdmin || busy}
                  onClick={() => void submitLinkExisting()}
                  className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                >
                  Lier
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {linkOrphanForClient ? (
          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4">
            <div className="w-full max-w-md rounded-xl border border-white/10 bg-slate-900 p-4 shadow-xl">
              <h3 className="text-lg font-semibold text-white">Lier à une fiche client</h3>
              <p className="text-sm text-slate-400">
                Utilisateur #{linkOrphanForClient.id} — {linkOrphanForClient.nom}
              </p>
              <select
                className="mt-4 w-full rounded-md border border-white/10 bg-slate-950 px-3 py-2 text-sm text-white"
                value={pickClientForOrphan}
                onChange={(e) => setPickClientForOrphan(e.target.value)}
              >
                <option value="">— Choisir un client —</option>
                {clientsPick.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nomAffiche} (#{c.id})
                  </option>
                ))}
              </select>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setLinkOrphanForClient(null)}
                  className="rounded-lg px-3 py-2 text-sm text-slate-300 hover:bg-white/5"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  disabled={!isAdmin || busy}
                  onClick={() => void submitLinkOrphanToClient()}
                  className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                >
                  Lier
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {resetClientId ? (
          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4">
            <div className="w-full max-w-md rounded-xl border border-white/10 bg-slate-900 p-4 shadow-xl">
              <h3 className="text-lg font-semibold text-white">Réinitialiser mot de passe</h3>
              <p className="text-sm text-slate-400">Client #{resetClientId}</p>
              <p className="mt-3 rounded-lg border border-emerald-500/25 bg-emerald-950/40 px-3 py-2 text-xs text-emerald-100/90">
                <span className="font-medium text-emerald-300">mustChangePassword</span> : après
                réinitialisation, le serveur impose le changement de mot de passe à la prochaine connexion
                portail (comportement actif côté API).
              </p>
              <label className="mt-4 block text-xs font-medium text-slate-400">
                Mot de passe temporaire
                <input
                  type="password"
                  placeholder="Minimum 6 caractères"
                  className="mt-1 w-full rounded-md border border-white/10 bg-slate-950 px-3 py-2 text-sm text-white"
                  value={resetPass}
                  onChange={(e) => setResetPass(e.target.value)}
                  autoComplete="new-password"
                />
              </label>
              <label className="mt-3 block text-xs font-medium text-slate-400">
                Confirmation
                <input
                  type="password"
                  placeholder="Saisir à nouveau"
                  className="mt-1 w-full rounded-md border border-white/10 bg-slate-950 px-3 py-2 text-sm text-white"
                  value={resetPassConfirm}
                  onChange={(e) => setResetPassConfirm(e.target.value)}
                  autoComplete="new-password"
                />
              </label>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setResetClientId(null);
                    setResetPass("");
                    setResetPassConfirm("");
                  }}
                  className="rounded-lg px-3 py-2 text-sm text-slate-300 hover:bg-white/5"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  disabled={!isAdmin || busy}
                  title={!isAdmin ? MSG_ADMIN : undefined}
                  onClick={() => void submitReset()}
                  className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                >
                  Réinitialiser
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
